'use client';
import { logger, LoggerEvents } from '@bw/core';
import { Button } from '@bw/elements';
import { LoaderBlue } from '@bw/elements/icons';
import { gtmEvent } from '@bw/ui';
import { ChangeEvent, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { SubmitHandler, useForm } from 'react-hook-form';
import { BaseForm } from '../base-form/BaseForm';
import styles from './ProducerPlacement.module.scss';

const FORM_ID = 'ProducerPlacementForm';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_MIME_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const isRequiredErrorMessage = 'This field is required';
const emailRegex =
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const phoneRegex =
    /^(\+?[1][-.● ]?)?\(?([2-9][0-9]{2})\)?[-.● ]?([2-9][0-9]{2})[-.● ]?([0-9]{4})$/;

export type ProducerPlacementFormData = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    zipCode: string;
    pcLicense: string;
    yearsSalesExperience: string;
    linkedInProfileLink?: string;
    resume: FileList;
    captchaToken: string;
};

export type RequestState =
    | { status: 'ready' | 'loading' | 'success' }
    | { status: 'error'; error: Error };

interface FormFieldConfig {
    name: keyof Omit<ProducerPlacementFormData, 'resume' | 'captchaToken'>;
    labelText: string;
    inputType: 'text' | 'email' | 'tel' | 'radio' | 'select';
    placeholder?: string;
    selectOptions?: string[];
    isRequired: boolean;
    validationRegex?: string;
    validationErrorMessage: string;
}

const formFields: FormFieldConfig[] = [
    {
        name: 'firstName',
        labelText: 'First Name',
        inputType: 'text',
        placeholder: 'First Name',
        isRequired: true,
        validationErrorMessage: 'Please enter a valid first name',
    },
    {
        name: 'lastName',
        labelText: 'Last Name',
        inputType: 'text',
        placeholder: 'Last Name',
        isRequired: true,
        validationErrorMessage: 'Please enter a valid last name',
    },
    {
        name: 'email',
        labelText: 'Email Address',
        inputType: 'email',
        placeholder: 'Email Address',
        isRequired: true,
        validationRegex: emailRegex.source,
        validationErrorMessage: 'Please enter a valid email',
    },
    {
        name: 'phoneNumber',
        labelText: 'Phone Number',
        inputType: 'tel',
        placeholder: 'Phone Number',
        isRequired: true,
        validationRegex: phoneRegex.source,
        validationErrorMessage: 'Enter a valid phone number in 123-456-7890 format',
    },
    {
        name: 'zipCode',
        labelText: 'Zip Code',
        inputType: 'text',
        placeholder: 'Zip Code',
        isRequired: true,
        validationRegex: '^(?!00000)\\d{5}$', // Should it be this? '^[0-9]{5}(?:-[0-9]{4})?$',
        validationErrorMessage: 'Invalid zip code',
    },
    {
        name: 'pcLicense',
        labelText: 'Do you currently hold a P&C insurance license?',
        inputType: 'select',
        placeholder: 'Do you currently hold a P&C insurance license?',
        selectOptions: ['Yes', 'No', 'In Progress'],
        isRequired: true,
        validationErrorMessage: 'Please select an option',
    },
    {
        name: 'yearsSalesExperience',
        labelText: 'Years of sales experience',
        inputType: 'select',
        placeholder: 'Select years of experience',
        selectOptions: ['Less than 1 year', '1-2 years', '3-5 years', '5+ years'],
        isRequired: true,
        validationErrorMessage: 'Please select your years of experience',
    },
];

export function ProducerPlacementClientForm() {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<ProducerPlacementFormData>();
    const [requestState, setRequestState] = useState<RequestState>({ status: 'ready' });
    const [hideForm, setHideForm] = useState(false);
    const { executeRecaptcha } = useGoogleReCaptcha();

    const getCaptchaToken = async (action: string): Promise<string> => {
        const message =
            'executeRecaptcha not available on form submit, possible react script loading error.';
        if (!executeRecaptcha) {
            logger.error(LoggerEvents.GeneralErrorWebApp, { message });
            return 'no recaptcha1';
        }
        try {
            return await executeRecaptcha(action);
        } catch (error) {
            logger.error(LoggerEvents.GeneralErrorWebApp, { message });
        }
        return 'no recaptcha1';
    };

    const onSubmit: SubmitHandler<ProducerPlacementFormData> = async (data) => {
        setRequestState({ status: 'loading' });
        setHideForm(true);

        try {
            const captchaToken = await getCaptchaToken(FORM_ID);

            const formData = new FormData();
            formData.append('firstName', data.firstName);
            formData.append('lastName', data.lastName);
            formData.append('email', data.email);
            formData.append('phoneNumber', data.phoneNumber);
            formData.append('zipCode', data.zipCode);
            formData.append('pcLicense', data.pcLicense);
            formData.append('yearsSalesExperience', data.yearsSalesExperience);
            if (data.linkedInProfileLink) {
                formData.append('linkedInProfileLink', data.linkedInProfileLink);
            }
            formData.append('captchaToken', captchaToken);
            if (data.resume?.[0]) {
                formData.append('resume', data.resume[0]);
            }

            const response = await fetch('/api/producer-placement', {
                method: 'POST',
                body: formData,
            });

            const result: { status: number; error?: string } = await response.json();

            if (typeof result.error === 'string') {
                throw new Error(result.error);
            }

            setRequestState({ status: 'success' });
            gtmEvent('form_success', { formName: FORM_ID });
            reset();
        } catch (error) {
            if (error instanceof Error) {
                setRequestState({ status: 'error', error });
                setHideForm(false);
            }
        }
    };

    const clearErrorState = () => {
        if (requestState.status === 'error') {
            setRequestState({ status: 'ready' });
        }
    };

    const ErrorDisplay = ({ state }: { state: RequestState }) => (
        <>
            {state.status === 'error' && (
                <div className={styles.error}>{state.error.message}</div>
            )}
        </>
    );

    const StateDisplay = ({ state }: { state: RequestState }) => (
        <>
            {state.status === 'loading' && (
                <div className="is__pageloader">
                    <figure>
                        <LoaderBlue width={100} height={100} />
                    </figure>
                </div>
            )}
        </>
    );

    const ThanksDisplay = ({ state }: { state: RequestState }) => (
        <>
            {state.status === 'success' && (
                <div className={styles.success}>
                    <h5 className="headingTagline">Application Received</h5>
                    <h2>Thank You!</h2>
                    <p>
                        We've received your application, and our recruiting team will review it
                        shortly. If your background and location match one of our open
                        agencies, you'll hear from us within 2 business days.
                    </p>
                    <p>
                        In the meantime, learn more at{' '}
                        <a href="https://www.brightway.com/why" target="_blank" rel="noopener noreferrer">
                            Brightway.com/why
                        </a>
                        .
                    </p>
                </div>
            )}
        </>
    );

    return (
        <div className={styles.formContainer}>
            {requestState.status === 'success' ? (
                <ThanksDisplay state={requestState} />
            ) : !hideForm ? (
                    <BaseForm
                        formId={FORM_ID}
                        onSubmit={handleSubmit(onSubmit)}
                        onChange={clearErrorState}
                    >
                        {/* Standard text / email / tel / radio / select fields */}
                        {formFields.map((field, index) => {
                            const {
                                name,
                                labelText,
                                inputType,
                                placeholder,
                                selectOptions,
                                isRequired,
                                validationErrorMessage,
                                validationRegex,
                            } = field;

                            const inputDataTestId = labelText
                                .replace(/\s/g, '-')
                                .replace(/[^a-zA-Z-]/g, '')
                                .toLowerCase();

                            const pattern = validationRegex
                                ? {
                                    value: RegExp(validationRegex),
                                    message: validationErrorMessage,
                                }
                                : undefined;

                            const renderField = () => {
                                switch (inputType) {
                                    case 'text':
                                        return (
                                            <>
                                                <input
                                                    id={name}
                                                    type="text"
                                                    data-testid={inputDataTestId}
                                                    {...register(name, {
                                                        required: isRequired
                                                            ? isRequiredErrorMessage
                                                            : false,
                                                        pattern,
                                                    })}
                                                    placeholder={placeholder ?? ''}
                                                />
                                                <span data-testid={`${inputDataTestId}-error`}>
                                                    {errors[name]?.message ?? ''}
                                                </span>
                                            </>
                                        );
                                    case 'email':
                                        return (
                                            <>
                                                <input
                                                    id={name}
                                                    type="text"
                                                    data-testid={inputDataTestId}
                                                    {...register(name, {
                                                        required: isRequired
                                                            ? isRequiredErrorMessage
                                                            : false,
                                                        pattern,
                                                    })}
                                                    placeholder={placeholder ?? ''}
                                                />
                                                <span data-testid={`${inputDataTestId}-error`}>
                                                    {errors[name]?.message ?? ''}
                                                </span>
                                            </>
                                        );
                                    case 'tel':
                                        return (
                                            <>
                                                <input
                                                    id={name}
                                                    type="tel"
                                                    data-testid={inputDataTestId}
                                                    {...register(name as any, {
                                                        required: isRequired
                                                            ? isRequiredErrorMessage
                                                            : false,
                                                        pattern,
                                                    })}
                                                    placeholder={placeholder ?? ''}
                                                />
                                                <span data-testid={`${inputDataTestId}-error`}>
                                                    {errors[name]?.message ?? ''}
                                                </span>
                                            </>
                                        );
                                    case 'select':
                                        return (
                                            <>
                                                <select
                                                    id={name}
                                                    data-testid={inputDataTestId}
                                                    {...register(name, {
                                                        required: isRequired
                                                            ? isRequiredErrorMessage
                                                            : false,
                                                    })}
                                                >
                                                    <option value="">{placeholder ?? 'Select'}</option>
                                                    {selectOptions?.map((option, i) => (
                                                        <option key={i} value={option.trim()}>
                                                            {option}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span data-testid={`${inputDataTestId}-error`}>
                                                    {errors[name]?.message ?? ''}
                                                </span>
                                            </>
                                        );
                                    case 'radio':
                                        return (
                                            <>
                                                <h3>{placeholder}</h3>
                                                {selectOptions?.map((option, i) => {
                                                    const radioId = `${name}-${i}`;
                                                    return (
                                                        <label key={i} htmlFor={radioId}>
                                                            <input
                                                                type="radio"
                                                                id={radioId}
                                                                data-testid={`${inputDataTestId}-${i}`}
                                                                value={option.trim()}
                                                                {...register(name, {
                                                                    required: isRequired
                                                                        ? isRequiredErrorMessage
                                                                        : false,
                                                                })}
                                                            />
                                                            {option}
                                                        </label>
                                                    );
                                                })}
                                                <span data-testid={`${inputDataTestId}-error`}>
                                                    {errors[name]?.message ?? ''}
                                                </span>
                                            </>
                                        );
                                    default:
                                        return null;
                                }
                            };

                            return (
                                <div
                                    key={index}
                                    className={styles.formField}
                                    data-aos="fade-up"
                                >
                                    {renderField()}
                                </div>
                            );
                        })}

                        {/* LinkedIn Profile Link */}
                        <div className={styles.formField} data-aos="fade-up">
                            <input
                                id="linkedInProfileLink"
                                type="text"
                                data-testid="linkedin-profile-link"
                                {...register('linkedInProfileLink')}
                                placeholder="LinkedIn Profile Link (optional)"
                            />
                            <span data-testid="linkedin-profile-link-error">
                                {errors.linkedInProfileLink?.message ?? ''}
                            </span>
                        </div>

                        {/* Resume upload */}
                        <div className={styles.fileField} data-aos="fade-up">
                            <label htmlFor="resume">Resume (.pdf or .docx, max 5MB, optional)</label>
                            <input
                                id="resume"
                                type="file"
                                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                data-testid="resume"
                                {...register('resume', {
                                    validate: {
                                        maxSize: (files: FileList) =>
                                            !files?.[0] ||
                                            files[0].size <= MAX_FILE_SIZE ||
                                            'File must be under 5MB',
                                        acceptedFormat: (files: FileList) =>
                                            !files?.[0] ||
                                            ACCEPTED_MIME_TYPES.includes(files[0].type) ||
                                            'Only PDF and DOCX files are accepted',
                                    },
                                })}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    register('resume').onChange(e);
                                    clearErrorState();
                                }}
                            />
                            <span className={styles.fileError} data-testid="resume-error">
                                {errors.resume?.message ?? ''}
                            </span>
                        </div>

                        <Button
                            id={`${FORM_ID}-submit`}
                            buttonStyle="accent"
                            type="submit"
                            title="Submit My Application →"
                            disabled={requestState.status === 'loading'}
                            data-aos="fade-up"
                        />

                        <p className={styles.reassurance}>
                            Our recruiting team personally reviews every application and will match
                            you with the agency opportunity that best fits your background and
                            location.
                        </p>

                        <ErrorDisplay state={requestState} />
                    </BaseForm>
            ) : (
                    <StateDisplay state={requestState} />
                )}
        </div>
    );
}
